import { useGameStore } from '@/store/gameStore'
import { SERVICES } from '@/config/servicesConfig'
import { getFacilityDef } from '@/config/facilityRegistry'
import { STAFF_DEFINITIONS, type StaffType } from '@/config/staffConfig'
import { calculateServiceDemand, areRequirementsMet } from '@/game/systems/DemandSystem'
import { GameIcon, SERVICE_ICONS } from '../icons/iconMap'
import { SectionDeck, EmptyStateRich } from '../components/CourtVision'
import { BroadcastHeader } from '../components/broadcast'
import { MechanicHint } from '../components/MechanicHint'
import { Lock, Briefcase, LandPlot, CircleDot, Power, Store } from 'lucide-react'

export function ServicesTabContent() {
  const { enabledServices, servicePrices, toggleService, setServicePrice, courts } = useGameStore()

  if (courts.length === 0) {
    return (
      <EmptyStateRich
        icon={LandPlot}
        title="Sin pistas no hay negocio"
        sub="Construye tu primera pista en Instalaciones para empezar a ofrecer servicios"
      />
    )
  }

  const totalDailyIncome = SERVICES
    .filter(s => enabledServices.includes(s.id))
    .reduce((sum, s) => sum + calculateServiceDemand(s.id).estimatedDailyIncome, 0)

  const active = SERVICES.filter(s => enabledServices.includes(s.id))
  const available = SERVICES.filter(s => !enabledServices.includes(s.id) && areRequirementsMet(s))
  const locked = SERVICES.filter(s => !enabledServices.includes(s.id) && !areRequirementsMet(s))

  return (
    <div>
      <BroadcastHeader
        title={`~${Math.round(totalDailyIncome).toLocaleString('es-ES')}€/día`}
        competition="Gestión comercial"
        icon={Store}
        meta={
          <>
            <span className="chip-angled tone-gold">{active.length} activo{active.length === 1 ? '' : 's'}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <MechanicHint
                title="Demanda del dia"
                bullets={[
                  'La demanda combina reputacion, clima, temporada, precio e instalaciones.',
                  'Subir el precio por encima del mercado reduce la ocupacion.',
                  'Mas personal y edificios elevan la demanda de todos los servicios.',
                ]}
              />
            </span>
          </>
        }
      />

      {active.length > 0 && (
        <SectionDeck title="En marcha" icon={Power} meta={`${active.length} servicio${active.length === 1 ? '' : 's'}`}>
          <div className="space-y-2">
            {active.map(service => (
              <ActiveServiceRow
                key={service.id}
                service={service}
                price={servicePrices[service.id] ?? service.basePrice}
                onToggle={() => toggleService(service.id)}
                onPrice={p => setServicePrice(service.id, p)}
              />
            ))}
          </div>
        </SectionDeck>
      )}

      {available.length > 0 && (
        <SectionDeck title="Listos para activar" icon={Briefcase}>
          <div className="space-y-2">
            {available.map(service => (
              <button key={service.id} className="row-tile" onClick={() => toggleService(service.id)}>
                <span className="icon-tile"><GameIcon domain="service" id={service.id} size={18} /></span>
                <div className="row-tile-main">
                  <div className="row-tile-title">{service.name}</div>
                  <div className="row-tile-sub">{service.description}</div>
                </div>
                <span className="btn btn-success" style={{ padding: '0.3rem 0.7rem', fontSize: '0.78rem' }}>
                  Activar
                </span>
              </button>
            ))}
          </div>
        </SectionDeck>
      )}

      {locked.length > 0 && (
        <SectionDeck title="Por desbloquear" icon={Lock}>
          <div className="space-y-2">
            {locked.map(service => (
              <div key={service.id} className="row-tile" style={{ opacity: 0.65 }}>
                <span className="icon-tile tone-muted"><GameIcon domain="service" id={service.id} size={18} /></span>
                <div className="row-tile-main">
                  <div className="row-tile-title">{service.name}</div>
                  <div className="row-tile-sub">{service.description}</div>
                </div>
                <RequirementsList service={service} />
              </div>
            ))}
          </div>
        </SectionDeck>
      )}
    </div>
  )
}

function ActiveServiceRow({ service, price, onToggle, onPrice }: {
  service: typeof SERVICES[0]
  price: number
  onToggle: () => void
  onPrice: (price: number) => void
}) {
  const demand = calculateServiceDemand(service.id)
  const ServiceIcon = SERVICE_ICONS[service.id] ?? CircleDot
  const demandTone = demand.finalDemand > 0.7 ? 'success' : demand.finalDemand > 0.4 ? 'warning' : 'danger'

  return (
    <div className="row-tile" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.55rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
        <span className="icon-tile tone-success"><ServiceIcon size={18} /></span>
        <div className="row-tile-main">
          <div className="row-tile-title">{service.name}</div>
          <div className="row-tile-sub">{service.description}</div>
        </div>
        <div className="row-tile-side">
          <span className="money-positive" style={{ fontWeight: 700 }}>
            ~{Math.round(demand.estimatedDailyIncome).toLocaleString('es-ES')}€/dia
          </span>
          <button className="icon-btn" onClick={onToggle} style={{ fontSize: '0.7rem', fontWeight: 600 }}>
            <Power size={11} /> Apagar
          </button>
        </div>
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.6rem',
        borderTop: '1px solid var(--border-default)', paddingTop: '0.5rem',
      }}>
        <span className="meta-text" style={{ minWidth: '54px' }}>Demanda</span>
        <div className="progress-track tall">
          <div className={`progress-fill angled ${demandTone}`} style={{ width: `${demand.finalDemand * 100}%` }} />
        </div>
        <span className="meta-text" style={{ minWidth: '32px', textAlign: 'right', fontWeight: 600 }}>
          {Math.round(demand.finalDemand * 100)}%
        </span>
      </div>

      {service.id === 'court_rental' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span className="meta-text" style={{ minWidth: '54px' }}>Precio</span>
          <input
            type="range"
            min={10}
            max={50}
            value={price}
            onChange={e => onPrice(Number(e.target.value))}
            style={{ flex: 1, accentColor: 'var(--gold)' }}
          />
          <span className="money-positive" style={{ fontSize: '0.78rem', fontWeight: 700, minWidth: '48px', textAlign: 'right' }}>
            {price}€/h
          </span>
        </div>
      )}
    </div>
  )
}

function RequirementsList({ service }: { service: typeof SERVICES[0] }) {
  const store = useGameStore()

  const unmet = service.requirements.filter(req => {
    switch (req.type) {
      case 'courts': return store.courts.length < (req.value as number)
      case 'building': return !store.buildings.some(b => b.type === req.value)
      case 'staff': return !store.staff.some(s => s.type === req.value)
      case 'reputation': return store.reputation < (req.value as number)
      default: return false
    }
  })

  const describe = (req: typeof service.requirements[0]): string => {
    switch (req.type) {
      case 'courts': return `${req.value} pista${(req.value as number) > 1 ? 's' : ''}`
      case 'building': return `Requiere ${getFacilityDef(req.value as string)?.name ?? req.value}`
      case 'staff': return `Requiere ${STAFF_DEFINITIONS[req.value as StaffType]?.name ?? req.value}`
      case 'reputation': return `Reputacion ${req.value}+`
      default: return String(req.value)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-end' }}>
      {unmet.map((req, i) => (
        <span key={i} className="pill pill-warning" style={{ whiteSpace: 'nowrap' }}>
          <Lock size={10} /> {describe(req)}
        </span>
      ))}
    </div>
  )
}

